import type { ShipType } from '@stars-and-ships/api-types';
import { formatNumber, SHIP_LABELS, SHIP_ORDER } from '../../labels';
import { Button, NumberField } from '../ui';

/**
 * One row per ship class stationed at the origin: count, available, All and Clear. Ships held back
 * by timed orders (G-67) are not in `available`; `reserved` names them in the row's hint.
 */
export function ShipRows({
  available,
  reserved = {},
  counts,
  onChange,
}: {
  available: Partial<Record<ShipType, number>>;
  reserved?: Partial<Record<ShipType, number>>;
  counts: Partial<Record<ShipType, string>>;
  onChange(type: ShipType, value: string): void;
}) {
  const types = SHIP_ORDER.filter((t) => (available[t] ?? 0) > 0 || (reserved[t] ?? 0) > 0);
  return (
    <ul className="space-y-3">
      {types.map((type) => (
        <li key={type} data-ship-row={type} className="flex items-end gap-2">
          <NumberField
            className="min-w-0 flex-1"
            label={SHIP_LABELS[type]}
            aria-label={`Send ${SHIP_LABELS[type]}`}
            hint={
              `${formatNumber(available[type] ?? 0)} available` +
              (reserved[type] ? ` · ${formatNumber(reserved[type])} reserved` : '')
            }
            min={0}
            max={available[type] ?? 0}
            value={counts[type] ?? ''}
            onChange={(e) => onChange(type, e.target.value)}
          />
          <div className="mb-5 flex gap-1">
            <Button
              variant="secondary"
              aria-label={`All ${SHIP_LABELS[type]}`}
              disabled={(available[type] ?? 0) === 0}
              onClick={() => onChange(type, String(available[type] ?? 0))}
            >
              All
            </Button>
            <Button
              variant="ghost"
              aria-label={`Clear ${SHIP_LABELS[type]}`}
              disabled={!counts[type]}
              onClick={() => onChange(type, '')}
            >
              Clear
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
